import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import axios from "axios";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`/api/contact.php`, form);
      setStatus("sent");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-16 md:pt-0">
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs font-bold tracking-[0.2em] text-[#2DD4BF] uppercase mb-3">Get in Touch</p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3">Contact Us</h1>
            <p className="text-[#94A3B8] max-w-2xl">
              Have questions about our products or want to partner with us? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="px-6 pb-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact info */}
          <div className="lg:col-span-2">
            <ScrollReveal>
              <div className="space-y-6">
                <a
                  href="mailto:info@drukstbiotech.com"
                  data-testid="contact-email-link"
                  className="group flex items-start gap-4 p-5 rounded-xl border border-white/5 bg-white/[0.02] hover:border-[#2DD4BF]/30 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#2DD4BF]/10 flex items-center justify-center shrink-0">
                    <Mail size={18} className="text-[#2DD4BF]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8] mb-1">Email Us</p>
                    <p className="text-sm text-white font-medium group-hover:text-[#2DD4BF] transition-colors">
                      info@drukstbiotech.com
                    </p>
                  </div>
                </a>

                <a
                  href="tel:+919250019785"
                  data-testid="contact-phone-link"
                  className="group flex items-start gap-4 p-5 rounded-xl border border-white/5 bg-white/[0.02] hover:border-[#2DD4BF]/30 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#2DD4BF]/10 flex items-center justify-center shrink-0">
                    <Phone size={18} className="text-[#2DD4BF]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8] mb-1">Call Us</p>
                    <p className="text-sm text-white font-medium group-hover:text-[#2DD4BF] transition-colors">
                      +91 9250019785
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-5 rounded-xl border border-white/5 bg-white/[0.02]">
                  <div className="w-10 h-10 rounded-lg bg-[#2DD4BF]/10 flex items-center justify-center shrink-0">
                    <MapPin size={18} className="text-[#2DD4BF]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8] mb-1">Visit Us</p>
                    <p className="text-sm text-white font-medium" data-testid="contact-address">
                      Drukst Biotech Pvt. Ltd.,<br />
                      Idar Highway Road, Dhandha,<br />
                      Songadh, Himatnagar 383001,<br />
                      Gujarat, India
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-3">
            <ScrollReveal delay={100}>
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2 block">Name</label>
                    <input
                      type="text"
                      name="name"
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2 block">Email</label>
                    <input
                      type="email"
                      name="email"
                      data-testid="contact-email-input"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2 block">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full h-12 px-4 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] transition-all"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2 block">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      data-testid="contact-subject-input"
                      value={form.subject}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] transition-all"
                      placeholder="How can we help?"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2 block">Message</label>
                  <textarea
                    name="message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] transition-all resize-none"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={status === "sending"}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#2DD4BF] text-[#022C22] font-bold text-sm hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
                >
                  {status === "sending" ? (
                    <>Sending...</>
                  ) : status === "sent" ? (
                    <><CheckCircle2 size={16} /> Message Sent!</>
                  ) : (
                    <><Send size={16} /> Send Message</>
                  )}
                </button>

                {status === "error" && (
                  <p className="text-sm text-red-400" data-testid="contact-error">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}
                {status === "sent" && (
                  <p className="text-sm text-[#2DD4BF]" data-testid="contact-success">
                    Thank you! We'll get back to you soon.
                  </p>
                )}
              </form>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
